import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, LineChart } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  post_id: "", platform: "Instagram", format: "Reel", funnel_stage: "TOF",
  hook: "", topic: "", post_time: "",
  views: 0, retention_pct: 0, shares: 0, saves: 0, comments: 0,
  follows: 0, profile_visits: 0, clicks: 0, leads: 0, sales: 0, notes: "",
};

const NUM_FIELDS = ["views","retention_pct","shares","saves","comments","follows","profile_visits","clicks","leads","sales"];

export default function Performance() {
  const [metrics, setMetrics] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/api/performance/list");
    setMetrics(data);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      NUM_FIELDS.forEach((k) => { payload[k] = Number(payload[k]) || 0; });
      await api.post("/api/performance/import", payload);
      toast.success("Metric imported");
      setForm(EMPTY);
      await load();
    } catch (e) { toast.error("Import failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/api/performance/${id}`);
    await load();
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Performance Analyst</div>
          <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">What actually worked.</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">Import each post's metrics manually. ContentOS finds the patterns and rewrites next week's plan.</p>
        </div>
      </div>

      <div className="p-8 grid grid-cols-12 gap-4">
        <form onSubmit={submit} className="col-span-12 lg:col-span-5 border border-border rounded p-6 bg-card space-y-4" data-testid="perf-form">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Import a post</div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Topic</Label>
              <Input value={form.topic} onChange={(e) => set("topic", e.target.value)} className="h-10 mt-1" data-testid="perf-topic" required/>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Hook (the opening line)</Label>
              <Input value={form.hook} onChange={(e) => set("hook", e.target.value)} className="h-10 mt-1" data-testid="perf-hook" required/>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest">Platform</Label>
              <Select value={form.platform} onValueChange={(v) => set("platform", v)}>
                <SelectTrigger className="h-10 mt-1" data-testid="perf-platform"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["Instagram","LinkedIn","YouTube","TikTok","X"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Format</Label>
              <Select value={form.format} onValueChange={(v) => set("format", v)}>
                <SelectTrigger className="h-10 mt-1" data-testid="perf-format"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["Reel","Carousel","Story","Long-form post","Short video","YouTube Short","Thread"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Funnel</Label>
              <Select value={form.funnel_stage} onValueChange={(v) => set("funnel_stage", v)}>
                <SelectTrigger className="h-10 mt-1" data-testid="perf-funnel"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["TOF","MOF","BOF"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Post time</Label>
              <Input value={form.post_time} onChange={(e) => set("post_time", e.target.value)} className="h-10 mt-1" placeholder="e.g. Tue 8:30 AM" data-testid="perf-time"/>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
            {[
              ["views", "Views"], ["retention_pct", "Retention %"], ["shares", "Shares"],
              ["saves", "Saves"], ["comments", "Comments"], ["follows", "Follows"],
              ["profile_visits", "Profile visits"], ["clicks", "Clicks"], ["leads", "Leads"], ["sales", "Sales"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">{l}</Label>
                <Input type="number" step={k === "retention_pct" ? "0.1" : "1"} value={form[k]} onChange={(e) => set(k, e.target.value)} className="h-9 mt-1" data-testid={`perf-${k}`}/>
              </div>
            ))}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Notes (optional)</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 min-h-[70px]" data-testid="perf-notes"/>
          </div>

          <Button type="submit" disabled={busy} className="w-full h-11" data-testid="btn-import-metric">
            <Plus className="h-4 w-4"/> {busy ? "Importing…" : "Import metric"}
          </Button>
        </form>

        <div className="col-span-12 lg:col-span-7 border border-border rounded bg-card overflow-hidden" data-testid="perf-list">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{metrics.length} posts tracked</div>
            <LineChart className="h-4 w-4 text-primary"/>
          </div>
          {metrics.length === 0 ? (
            <div className="p-10 text-sm text-muted-foreground">No metrics yet — import your first post to unlock the AI Weekly Review.</div>
          ) : (
            <div className="max-h-[720px] overflow-auto divide-y divide-border">
              {metrics.map((m) => (
                <div key={m.id} className="p-4 hover:bg-secondary/50 transition-colors duration-150" data-testid={`metric-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        <span className="pillar-badge">{m.funnel_stage}</span>
                        <span className="pillar-badge">{m.platform}</span>
                        <span className="pillar-badge">{m.format}</span>
                      </div>
                      <div className="mt-2 font-display font-bold leading-tight">{m.topic}</div>
                      <div className="text-sm text-muted-foreground mt-1">“{m.hook}”</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => del(m.id)} data-testid={`btn-del-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5"/>
                    </Button>
                  </div>
                  <div className="mt-3 grid grid-cols-5 gap-2 text-xs">
                    {[
                      ["views", "Views"], ["retention_pct", "Ret %"], ["shares", "Shares"], ["saves", "Saves"], ["comments", "Comm"],
                    ].map(([k, l]) => (
                      <div key={k}>
                        <div className="text-muted-foreground font-mono text-[10px] uppercase">{l}</div>
                        <div className="font-semibold">{m[k]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
